import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useQueryState, parseAsInteger, parseAsString } from 'nuqs';
import { useCallback, useMemo } from 'react';
import { classService } from '../../../services';
import {
  DEFAULT_ADMIN_LIST_SORT,
  normalizeAdminListSort,
} from '../../../lib/admin-list-sort';
import type { 
  Class, 
  ClassFilters, 
  ClassCreateRequest, 
  ClassUpdateRequest,
  ClassesResponse,
  ServerFiltersParams
} from '../types';

// URL state parsers
const schoolIdParser = parseAsString.withDefault('');
const subjectParser = parseAsString.withDefault('');
const gradeParser = parseAsString.withDefault('');
const schoolYearParser = parseAsString.withDefault('');
const statusParser = parseAsString.withDefault('');
const searchParser = parseAsString.withDefault('');
const sortParser = parseAsString.withDefault(DEFAULT_ADMIN_LIST_SORT);
const pageParser = parseAsInteger.withDefault(1);
const perPageParser = parseAsInteger.withDefault(10);

// Type cho query key
type ClassQueryKey = ['classes', ClassFilters, { page: number; per_page: number }];

const useClasses = () => {
  const queryClient = useQueryClient();

  // URL state management với nuqs
  const [schoolId, setSchoolId] = useQueryState('schoolId', schoolIdParser);
  const [subject, setSubject] = useQueryState('subject', subjectParser);
  const [grade, setGrade] = useQueryState('grade', gradeParser);
  const [schoolYear, setSchoolYear] = useQueryState('schoolYear', schoolYearParser);
  const [status, setStatus] = useQueryState('status', statusParser);
  const [search, setSearch] = useQueryState('search', searchParser);
  const [sort, setSort] = useQueryState('sort', sortParser);
  const [page, setPage] = useQueryState('page', pageParser);
  const [per_page, setPerPage] = useQueryState('per_page', perPageParser);

  // Tạo filters object từ URL state
  const filters: ClassFilters = useMemo(() => {
    const filterObj = {
      schoolId: schoolId && schoolId !== '' ? schoolId : undefined,
      subject: subject && subject !== '' ? subject : undefined,
      grade: grade && grade !== '' ? grade : undefined,
      schoolYear: schoolYear && schoolYear !== '' ? schoolYear : undefined,
      status: status && status !== '' ? (status as 'active' | 'inactive' | 'archived') : undefined,
      search: search && search !== '' ? search : undefined,
      sort: normalizeAdminListSort(sort),
    };
    console.log('useClasses - Filters updated:', filterObj, { schoolId, subject, grade, schoolYear, status, search, sort });
    return filterObj;
  }, [schoolId, subject, grade, schoolYear, status, search, sort]);

  // Query key cho caching
  const queryKey: ClassQueryKey = ['classes', filters, { page, per_page }];
  console.log('useClasses - Query key:', queryKey);

  // Fetch classes với react-query
  const {
    data: classesData,
    isLoading,
    isError,
    error,
    refetch,
    isFetching,
  } = useQuery({
    queryKey,
    queryFn: () => {
      console.log('useClasses - Query triggered with:', { filters, page, per_page });
      return classService.getClasses({ 
        ...filters, 
        page, 
        per_page 
      });
    },
    staleTime: 5 * 60 * 1000, // 5 phút
    gcTime: 10 * 60 * 1000, // 10 phút (cacheTime cũ)
  });

  // Fetch single class
  const useClassDetail = (id: string) => {
    return useQuery({
      queryKey: ['class', id],
      queryFn: () => classService.getClass(id),
      enabled: !!id,
      staleTime: 5 * 60 * 1000,
    });
  };

  // Create class mutation
  const createClassMutation = useMutation({
    mutationFn: (data: ClassCreateRequest) => classService.createClass(data),
    onSuccess: (newClass) => {
      // Invalidate và refetch classes list
      queryClient.invalidateQueries({ queryKey: ['classes'] });
    },
  });

  // Update class mutation
  const updateClassMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: ClassUpdateRequest }) =>
      classService.updateClass(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['class', id] });
      queryClient.invalidateQueries({ queryKey: ['classes'] });
    },
  });

  // Delete class mutation
  const deleteClassMutation = useMutation({
    mutationFn: (id: string) => classService.deleteClass(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['classes'] });
    },
  });

  // Toggle class status mutation
  const toggleStatusMutation = useMutation({
    mutationFn: (id: string) => classService.toggleClassStatus(id),
    onSuccess: (updatedClass) => {
      queryClient.setQueryData(['class', updatedClass.id], updatedClass);
      queryClient.invalidateQueries({ queryKey: ['classes'] });
    },
  });

  // Filter và pagination handlers
  const updateFilters = useCallback((newFilters: Partial<ClassFilters>) => {
    console.log('updateFilters called with:', newFilters);
    if (newFilters.schoolId !== undefined) {
      console.log('Setting schoolId:', newFilters.schoolId);
      setSchoolId(newFilters.schoolId || '');
    }
    if (newFilters.subject !== undefined) setSubject(newFilters.subject || '');
    if (newFilters.grade !== undefined) setGrade(newFilters.grade || '');
    if (newFilters.schoolYear !== undefined) setSchoolYear(newFilters.schoolYear || '');
    if (newFilters.status !== undefined) setStatus(newFilters.status || '');
    if (newFilters.search !== undefined) setSearch(newFilters.search || '');
    if (newFilters.sort !== undefined) setSort(normalizeAdminListSort(newFilters.sort));
    // Reset về trang 1 khi filter thay đổi
    setPage(1);
  }, [setSchoolId, setSubject, setGrade, setSchoolYear, setStatus, setSearch, setSort, setPage]);

  const updatePagination = useCallback((newPage: number, newPerPage?: number) => {
    setPage(newPage);
    if (newPerPage) setPerPage(newPerPage);
  }, [setPage, setPerPage]);

  const resetFilters = useCallback(() => {
    setSchoolId('');
    setSubject('');
    setGrade('');
    setSchoolYear('');
    setStatus('');
    setSearch('');
    setSort(null);
    setPage(1);
  }, [setSchoolId, setSubject, setGrade, setSchoolYear, setStatus, setSearch, setSort, setPage]);

  // Clear specific filter
  const clearFilter = useCallback((filterKey: keyof ClassFilters) => {
    switch (filterKey) {
      case 'schoolId':
        setSchoolId('');
        break;
      case 'subject':
        setSubject('');
        break;
      case 'grade':
        setGrade('');
        break;
      case 'schoolYear':
        setSchoolYear('');
        break;
      case 'status':
        setStatus('');
        break;
      case 'search':
        setSearch('');
        break;
      case 'sort':
        setSort(null);
        break;
    }
    setPage(1);
  }, [setSchoolId, setSubject, setGrade, setSchoolYear, setStatus, setSearch, setSort, setPage]);

  // Parse pagination from backend response
  const pagination = useMemo(() => {
    if (!classesData) return undefined;
    
    // Backend returns pagination in different formats depending on whether pagination is used
    if (classesData.pagination) {
      // Laravel paginator format
      return {
        page: classesData.pagination.current_page || classesData.pagination.page || 1,
        per_page: classesData.pagination.per_page || 10,
        total: classesData.pagination.total || 0,
        totalPages: classesData.pagination.last_page || classesData.pagination.totalPages || 1,
      };
    }
    
    // If no pagination info, assume single page
    return {
      page: 1,
      per_page: classesData.data?.length || 0,
      total: classesData.data?.length || 0,
      totalPages: 1,
    };
  }, [classesData]);

  return {
    // Data
    classes: classesData?.data || [],
    pagination,
    
    // Loading states
    isLoading,
    isFetching,
    isError,
    error,

    // Current filters và pagination
    filters,
    page,
    per_page,

    // Actions
    refetch,
    updateFilters,
    updatePagination,
    resetFilters,
    clearFilter,

    // Mutations
    createClass: createClassMutation.mutate,
    updateClass: updateClassMutation.mutate,
    deleteClass: deleteClassMutation.mutate,
    toggleStatus: toggleStatusMutation.mutate,

    // Mutation states
    isCreating: createClassMutation.isPending,
    isUpdating: updateClassMutation.isPending,
    isDeleting: deleteClassMutation.isPending,
    isTogglingStatus: toggleStatusMutation.isPending,

    // Mutation objects for advanced handling
    createClassMutation,
    updateClassMutation,
    deleteClassMutation,
    toggleStatusMutation,

    // Single class hook
    useClassDetail,
  };
};

export default useClasses;
